import { EventId, RegionId, TaskTypeId } from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { browserTimeZone } from "components/calendar/calendar-utils";
import { trpc } from "components/rpc/trpc";
import { useActivityTypeOptions } from "components/tasks/activity-type-options";
import { InlineBanner } from "components/ui/inline-banner";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { SecondaryButton } from "components/ui/secondary-button";
import React, { useCallback, useMemo, useState } from "react";
import { useCurrentUserId, useRegionId } from "store/selectors/hooks";
import { newEventDefaults } from "./new-event-defaults";

interface NewEventPanelProps {
    onCreated: (eventId: EventId) => void;
    onClose: () => void;
}

export const NewEventPanel: React.FC<NewEventPanelProps> = props => {
    const { onCreated, onClose } = props;
    const regionId = useRegionId();
    const currentUserId = useCurrentUserId();
    const trpcClient = trpc.useTRPC();
    const options = useActivityTypeOptions();
    const eventTypes = useMemo(
        () => options.filter(option => option.category === "event" && !option.isArchived),
        [options],
    );
    const [error, setError] = useState<string | null>(null);

    const createMutation = useMutation({
        ...trpcClient.event.create.mutationOptions(),
        onSuccess: result => onCreated(result.id),
        onError: () => setError("Could not create the event. Please try again."),
    });

    const handleTypeSelected = useCallback(
        (typeId: TaskTypeId, typeName: string) => {
            setError(null);
            const timeZone = browserTimeZone();
            const defaults = newEventDefaults(typeName, timeZone, new Date());
            createMutation.mutate({
                regionId: regionId ?? ("" as RegionId),
                activityTypeId: typeId,
                title: defaults.title,
                description: null,
                schedule: {
                    startsAt: defaults.startsAt,
                    timeZone,
                    eventDuration: defaults.eventDuration,
                },
                address: {
                    location: null,
                    street: null,
                    city: null,
                    state: null,
                    zip: null,
                },
                ownerUserIds: currentUserId ? [currentUserId] : [],
                invitees: [],
            });
        },
        [createMutation, regionId, currentUserId],
    );

    return (
        <Panel
            isOpen={true}
            type={PanelType.medium}
            headerText="New event"
            onDismiss={onClose}>
            <div className="space-y-3 p-4">
                <p className="text-sm text-gray-600">What kind of event is this?</p>
                <p className="text-xs text-gray-500">
                    Pick a type and the event is created right away — you set the title,
                    time, place, and guest list on the event page.
                </p>
                {error ? <InlineBanner type="error">{error}</InlineBanner> : null}
                {eventTypes.length === 0 ? (
                    <p className="text-sm text-gray-500">
                        No event types are set up yet. An administrator can add them under
                        Activity Type Settings.
                    </p>
                ) : (
                    <ul className="space-y-2">
                        {eventTypes.map(option => (
                            <li key={option.id}>
                                <SecondaryButton
                                    className="w-full justify-start"
                                    disabled={createMutation.isPending}
                                    onClick={() =>
                                        handleTypeSelected(option.id, option.name)
                                    }>
                                    {option.name}
                                </SecondaryButton>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </Panel>
    );
};
